import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import PredictionPaywall from "@/components/PredictionPaywall";
import { 
    Zap, 
    TrendingUp, 
    Calendar,
    AlertTriangle,
    Activity,
    ArrowLeft,
    ChevronRight
} from "lucide-react";

const CATEGORIES = [
    { id: "football", name: "Kandanda", nameEn: "Football" },
    { id: "basketball", name: "Mpira wa Kikapu", nameEn: "Basketball" },
    { id: "athletics", name: "Riadha", nameEn: "Athletics" },
    { id: "rugby", name: "Raga", nameEn: "Rugby" },
    { id: "cricket", name: "Kriketi", nameEn: "Cricket" },
    { id: "tennis", name: "Tenisi", nameEn: "Tennis" },
    { id: "boxing", name: "Ngumi", nameEn: "Boxing" },
];

const PredictionsPage = () => {
    const [predictions, setPredictions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSport, setSelectedSport] = useState(null);
    const [selectedPrediction, setSelectedPrediction] = useState(null);

    useEffect(() => {
        fetchPredictions();
    }, [selectedSport]);

    const fetchPredictions = async () => {
        try {
            let url = `${API}/predictions?limit=20&upcoming=true`;
            if (selectedSport) {
                url += `&sport=${selectedSport}`;
            }
            const response = await axios.get(url);
            setPredictions(response.data);
            if (response.data.length > 0 && !selectedPrediction) {
                setSelectedPrediction(response.data[0]);
            }
        } catch (error) {
            console.error("Error fetching predictions:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("sw-TZ", {
            weekday: "long",
            day: "numeric",
            month: "long",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getConfidenceColor = (score) => {
        if (score >= 75) return "text-electric";
        if (score >= 50) return "text-solar";
        return "text-red-500";
    };

    return (
        <div className="min-h-screen bg-obsidian" data-testid="predictions-page">
            {/* Navigation */}
            <nav className="nav-desktop">
                <div className="container-main">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <Zap className="h-8 w-8 text-electric" />
                            <span className="dimbani-wordmark text-2xl text-white">
                                DIMBANI
                            </span>
                        </Link>
                        <Link 
                            to="/" 
                            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Rudi Nyumbani
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative py-12 md:py-16 overflow-hidden" data-testid="predictions-hero">
                <div className="absolute inset-0 gradient-glow opacity-30" />
                <div className="container-main relative">
                    <div className="flex items-center gap-3 mb-4">
                        <TrendingUp className="h-8 w-8 text-electric" />
                        <h1 className="text-4xl md:text-6xl font-heading font-black text-white uppercase tracking-tight text-glow">
                            Utabiri wa Mechi
                        </h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-2xl">
                        Uchambuzi wa kina na utabiri unaotegemea data, majeraha ya wachezaji, na mwenendo wa timu.
                    </p>
                </div>
            </section>

            {/* Sport Filter */}
            <section className="border-y border-border/40 bg-paper/50">
                <div className="container-main py-4">
                    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                        <button
                            onClick={() => setSelectedSport(null)}
                            className={`px-4 py-2 rounded-sm font-heading font-bold uppercase text-sm whitespace-nowrap transition-colors ${
                                !selectedSport ? "bg-electric text-white" : "bg-paper text-white/70 hover:text-white"
                            }`}
                            data-testid="sport-all"
                        >
                            Yote
                        </button>
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedSport(cat.id)}
                                className={`px-4 py-2 rounded-sm font-heading font-bold uppercase text-sm whitespace-nowrap transition-colors ${
                                    selectedSport === cat.id ? "bg-electric text-white" : "bg-paper text-white/70 hover:text-white"
                                }`}
                                data-testid={`sport-${cat.id}`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="container-main py-12">
                {loading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <Card key={i} className="bg-paper border-border p-6 animate-pulse">
                                <div className="h-4 bg-muted rounded w-1/3 mb-4" />
                                <div className="h-6 bg-muted rounded w-2/3 mb-3" />
                                <div className="h-4 bg-muted rounded w-full mb-2" />
                                <div className="h-4 bg-muted rounded w-3/4" />
                            </Card>
                        ))}
                    </div>
                ) : predictions.length > 0 ? (
                    <PredictionPaywall matchCount={predictions.length}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Predictions List */}
                        <div className="lg:col-span-2 space-y-4">
                            {predictions.map((pred, idx) => (
                                <Card
                                    key={pred.id}
                                    className={`bg-paper border-border p-6 cursor-pointer transition-all hover:border-electric/50 ${
                                        selectedPrediction?.id === pred.id ? "border-electric" : ""
                                    }`}
                                    onClick={() => setSelectedPrediction(pred)}
                                    data-testid={`prediction-card-${idx}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            {/* Meta */}
                                            <div className="flex items-center gap-3 mb-3">
                                                <Badge className="bg-electric/10 text-electric border-electric/30">
                                                    {CATEGORIES.find(c => c.id === pred.sport)?.name || pred.sport}
                                                </Badge>
                                                <span className="text-white/40 text-sm flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {formatDate(pred.match_date)}
                                                </span>
                                            </div>

                                            {/* Teams */}
                                            <h3 className="text-xl font-heading font-bold text-white uppercase mb-2">
                                                {pred.home_team} vs {pred.away_team}
                                            </h3>

                                            {/* Prediction */}
                                            <p className="text-white/60 text-sm line-clamp-2">
                                                {pred.prediction_text_sw}
                                            </p>
                                        </div>

                                        {/* Confidence */}
                                        <div className="text-center shrink-0">
                                            <div className={`text-3xl font-heading font-black ${getConfidenceColor(pred.confidence_score)}`}>
                                                {pred.confidence_score}%
                                            </div>
                                            <p className="text-white/40 text-xs uppercase">Uhakika</p>
                                        </div>
                                    </div>

                                    {/* Key Factors Preview */}
                                    {pred.key_factors && pred.key_factors.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-border/40 flex flex-wrap gap-2">
                                            {pred.key_factors.slice(0, 3).map((factor, i) => (
                                                <span key={i} className="text-xs text-white/40 bg-subtle px-2 py-1 rounded">
                                                    {factor}
                                                </span>
                                            ))}
                                            {pred.key_factors.length > 3 && (
                                                <span className="text-xs text-electric">
                                                    +{pred.key_factors.length - 3} zaidi
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>

                        {/* Prediction Detail */}
                        <div className="lg:sticky lg:top-24 h-fit">
                            {selectedPrediction ? (
                                <Card className="bg-paper border-border overflow-hidden" data-testid="prediction-detail">
                                    {/* Header */}
                                    <div className="p-6 bg-subtle border-b border-border">
                                        <Badge className="mb-3 bg-electric text-white">
                                            {CATEGORIES.find(c => c.id === selectedPrediction.sport)?.name}
                                        </Badge>
                                        <h2 className="text-2xl font-heading font-black text-white uppercase">
                                            {selectedPrediction.home_team} vs {selectedPrediction.away_team}
                                        </h2>
                                        <p className="text-white/50 text-sm mt-2">
                                            {formatDate(selectedPrediction.match_date)}
                                        </p>
                                    </div>

                                    {/* Confidence */}
                                    <div className="p-6 border-b border-border">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-white/60 text-sm">Uhakika wa Utabiri</span>
                                            <span className={`font-bold ${getConfidenceColor(selectedPrediction.confidence_score)}`}>
                                                {selectedPrediction.confidence_score}%
                                            </span>
                                        </div>
                                        <Progress 
                                            value={selectedPrediction.confidence_score} 
                                            className="h-2 bg-subtle"
                                        />
                                    </div>

                                    {/* Prediction Text */}
                                    <div className="p-6 border-b border-border">
                                        <h4 className="text-white font-heading font-bold uppercase text-sm mb-3 flex items-center gap-2">
                                            <TrendingUp className="h-4 w-4 text-electric" />
                                            Utabiri
                                        </h4>
                                        <p className="text-white/80">
                                            {selectedPrediction.prediction_text_sw}
                                        </p>
                                    </div>

                                    {/* Key Factors */}
                                    {selectedPrediction.key_factors && selectedPrediction.key_factors.length > 0 && (
                                        <div className="p-6 border-b border-border">
                                            <h4 className="text-white font-heading font-bold uppercase text-sm mb-3 flex items-center gap-2">
                                                <Activity className="h-4 w-4 text-solar" />
                                                Mambo Muhimu
                                            </h4>
                                            <ul className="space-y-2">
                                                {selectedPrediction.key_factors.map((factor, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-white/60 text-sm">
                                                        <ChevronRight className="h-4 w-4 text-electric shrink-0 mt-0.5" />
                                                        {factor}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Injuries */}
                                    {(selectedPrediction.home_injuries?.length > 0 || selectedPrediction.away_injuries?.length > 0) && (
                                        <div className="p-6 border-b border-border">
                                            <h4 className="text-white font-heading font-bold uppercase text-sm mb-3 flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                                Majeraha
                                            </h4>
                                            <div className="space-y-3">
                                                {selectedPrediction.home_injuries?.length > 0 && (
                                                    <div>
                                                        <p className="text-white/50 text-xs mb-1">{selectedPrediction.home_team}</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {selectedPrediction.home_injuries.map((player, i) => (
                                                                <Badge key={i} variant="outline" className="border-red-500/30 text-red-400 text-xs">
                                                                    {player}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedPrediction.away_injuries?.length > 0 && (
                                                    <div>
                                                        <p className="text-white/50 text-xs mb-1">{selectedPrediction.away_team}</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {selectedPrediction.away_injuries.map((player, i) => (
                                                                <Badge key={i} variant="outline" className="border-red-500/30 text-red-400 text-xs">
                                                                    {player}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Form */}
                                    {(selectedPrediction.recent_form_home || selectedPrediction.recent_form_away) && (
                                        <div className="p-6">
                                            <h4 className="text-white font-heading font-bold uppercase text-sm mb-3">
                                                Mwenendo wa Hivi Karibuni
                                            </h4>
                                            <div className="space-y-2">
                                                {selectedPrediction.recent_form_home && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-white/60 text-sm">{selectedPrediction.home_team}</span>
                                                        <span className="font-mono text-electric">{selectedPrediction.recent_form_home}</span>
                                                    </div>
                                                )}
                                                {selectedPrediction.recent_form_away && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-white/60 text-sm">{selectedPrediction.away_team}</span>
                                                        <span className="font-mono text-solar">{selectedPrediction.recent_form_away}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            ) : (
                                <Card className="bg-paper border-border p-12 text-center">
                                    <TrendingUp className="h-16 w-16 text-electric/30 mx-auto mb-4" />
                                    <p className="text-white/40">Chagua utabiri kuona maelezo</p>
                                </Card>
                            )}
                        </div>
                    </div>
                    </PredictionPaywall>
                ) : (
                    <div className="text-center py-16">
                        <TrendingUp className="h-20 w-20 text-electric/20 mx-auto mb-4" />
                        <h2 className="text-2xl font-heading font-bold text-white uppercase mb-2">
                            Hakuna Utabiri Kwa Sasa
                        </h2>
                        <p className="text-white/50">
                            Utabiri mpya utaongezwa hivi karibuni
                        </p>
                    </div>
                )}
            </section>

            {/* Footer */}
            <footer className="border-t border-border/40 bg-paper/50">
                <div className="container-main py-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Zap className="h-6 w-6 text-electric" />
                            <span className="dimbani-wordmark text-xl text-white">DIMBANI</span>
                        </div>
                        <p className="text-white/40 text-sm">
                            &copy; {new Date().getFullYear()} Dimbani Sports
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PredictionsPage;
